import {
  CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH,
  CONTENT_ARTICLE_TOTAL_TEXT_MAX_LENGTH,
  CONTENT_DESCRIPTION_MAX_LENGTH,
  CONTENT_TITLE_MAX_LENGTH,
  CONTENT_VIDEO_FULL_DESCRIPTION_MAX_LENGTH,
} from '@/constants/contentLimits';
import { formatDomainLabel, getSubdomainsForDomain } from '@/constants/healthDomains';
import { TContentType } from '@/types/Content.types';
import { TBrandedFormErrors, TBrandedFormMode, TBrandedFormValues } from './types';

// Step 1 gate: title + at least one primary and one secondary domain are required to
// advance. For reels, a video source (upload or YouTube link) is also required here —
// the source field lives on this step. The remaining publish rules run at submit.
export const validateDetails = (values: TBrandedFormValues, type?: TContentType): TBrandedFormErrors => {
  const errors: TBrandedFormErrors = {};
  if (!values.title.trim()) {
    errors.title = 'Title is required.';
  } else if (values.title.length > CONTENT_TITLE_MAX_LENGTH) {
    errors.title = `Max ${CONTENT_TITLE_MAX_LENGTH} characters.`;
  }
  // For a post this field is the whole content — it is sent as both the description and
  // the body (see buildContentDetails), so the same rules cover both.
  if (values.description.length > CONTENT_DESCRIPTION_MAX_LENGTH) {
    errors.description = `Max ${CONTENT_DESCRIPTION_MAX_LENGTH} characters.`;
  }
  if (values.domains.length === 0) {
    errors.domains = 'Select at least one primary domain.';
  }
  if (values.subdomains.length === 0) {
    errors.subdomains = 'Select at least one secondary domain.';
  } else {
    // The backend requires every selected domain to be represented, not just that the
    // chosen subdomains belong to the selection — so a domain left uncovered is rejected.
    const uncoveredDomains = values.domains.filter(
      (domain) => !getSubdomainsForDomain(domain).some((subdomain) => values.subdomains.includes(subdomain)),
    );
    if (uncoveredDomains.length > 0) {
      errors.subdomains = `Select at least one secondary domain for: ${uncoveredDomains
        .map(formatDomainLabel)
        .join(', ')}.`;
    }
  }
  if (type === 'video') {
    const hasSource =
      values.videoSource === 'youtube' ? values.youtubeUrl.trim().length > 0 : Boolean(values.videoFile || values.muxStatus);
    if (!hasSource) {
      errors.video = values.videoSource === 'youtube' ? 'Add a YouTube link.' : 'Upload a video file.';
    }
  }
  return errors;
};

// Matches the labels the editor shows, so the error points at a block the author can find.
// The first section is the lead and has no header; the rest are "Section 1", "Section 2", ...
const sectionLabel = (index: number) => (index === 0 ? 'The lead section' : `Section ${index}`);

// The backend caps each section and each subsection independently, then caps the article
// total. Report the per-block breach first — it names a specific block, so it is more
// actionable than the total, and it is the rule the backend hits first too.
const findSectionTextError = (values: TBrandedFormValues): string | undefined => {
  for (const [index, section] of values.sections.entries()) {
    if (section.text.length > CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH) {
      return `${sectionLabel(index)} exceeds ${CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH} characters.`;
    }
    for (const [subIndex, subsection] of section.subsections.entries()) {
      if (subsection.text.length > CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH) {
        return `Sub Section ${subIndex + 1} of ${sectionLabel(index).toLowerCase()} exceeds ${CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH} characters.`;
      }
    }
  }
  return undefined;
};

const validateArticle = (values: TBrandedFormValues, requireAll: boolean, errors: TBrandedFormErrors) => {
  const totalText = values.sections.reduce(
    (sum, s) => sum + s.text.length + s.subsections.reduce((subSum, ss) => subSum + ss.text.length, 0),
    0,
  );
  const sectionTextError = findSectionTextError(values);
  if (sectionTextError) {
    errors.sections = sectionTextError;
  } else if (totalText > CONTENT_ARTICLE_TOTAL_TEXT_MAX_LENGTH) {
    errors.sections = `Total text exceeds ${CONTENT_ARTICLE_TOTAL_TEXT_MAX_LENGTH} characters.`;
  } else if (requireAll && values.sections.length === 0) {
    errors.sections = 'Add at least one section to publish.';
  } else if (requireAll && values.sections.some((s) => !s.text.trim())) {
    errors.sections = 'Every section needs text before publishing.';
  }
};

const validateVideo = (values: TBrandedFormValues, _requireAll: boolean, errors: TBrandedFormErrors) => {
  // The video source requirement is enforced at the Details gate (see validateDetails);
  // here we only bound the full description length.
  if (values.fullDescription.length > CONTENT_VIDEO_FULL_DESCRIPTION_MAX_LENGTH) {
    errors.fullDescription = `Max ${CONTENT_VIDEO_FULL_DESCRIPTION_MAX_LENGTH} characters.`;
  }
};

const TYPE_VALIDATORS: Partial<
  Record<TContentType, (values: TBrandedFormValues, requireAll: boolean, errors: TBrandedFormErrors) => void>
> = {
  article: validateArticle,
  video: validateVideo,
};

// Step 2 gate: the content step owns body / section text / full description, so bound them
// before the wizard moves on. Without this the limit error only appears at submit, by which
// point the author is on preview — a step that renders none of these fields. Length rules
// only here (requireAll = false), so an incomplete draft can still move forward.
export const validateContentStep = (values: TBrandedFormValues, type: TContentType): TBrandedFormErrors => {
  const errors: TBrandedFormErrors = {};
  TYPE_VALIDATORS[type]?.(values, false, errors);
  return errors;
};

export const validateForSubmit = (
  values: TBrandedFormValues,
  type: TContentType,
  mode: TBrandedFormMode,
): TBrandedFormErrors => {
  const errors = validateDetails(values, type);
  const requireAll = mode === 'publish';

  if (requireAll && !values.description.trim()) errors.description = 'Description is required to publish.';

  TYPE_VALIDATORS[type]?.(values, requireAll, errors);

  return errors;
};

// True when the validation errors belong to step 1 (Details), so the wizard can jump back.
export const hasDetailsError = (errors: TBrandedFormErrors): boolean =>
  Boolean(errors.title || errors.description || errors.domains || errors.subdomains || errors.video);
